import { Controller } from "@hotwired/stimulus"

// Owns the ops-portal alert sound. Card controllers dispatch
// `ops-alerts:ping` events with their DOM id; we play a tone only for
// ids we haven't seen before. That way initial-render cards are silent
// (seeded on connect) and replace broadcasts for already-known cards
// (state transitions, column moves) are silent too — only a genuinely
// new card makes noise.
export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    this.enabled = localStorage.getItem("opsAlertsEnabled") !== "false"
    this.lastPlayed = 0
    this.seenIds = new Set()
    this.audio = this.createAudio()

    // Pre-seed with every card already in the DOM at connect time.
    document.querySelectorAll("[data-controller~='new-card-alert']").forEach((el) => {
      if (el.id) this.seenIds.add(el.id)
    })

    this.boundOnPing = this.onPing.bind(this)
    window.addEventListener("ops-alerts:ping", this.boundOnPing)

    // AudioContext is suspended until a user gesture. Resume on first click.
    this.unlockAudio = () => {
      this.audio?._ctx?.resume?.()
      document.removeEventListener("click", this.unlockAudio, true)
    }
    document.addEventListener("click", this.unlockAudio, true)

    this.updateToggleUI()
  }

  disconnect() {
    window.removeEventListener("ops-alerts:ping", this.boundOnPing)
    if (this.unlockAudio) {
      document.removeEventListener("click", this.unlockAudio, true)
    }
  }

  onPing(event) {
    if (!this.enabled) return
    const id = event.detail?.id
    if (!id) return
    if (this.seenIds.has(id)) return  // replacement — not a new card
    this.seenIds.add(id)

    const now = Date.now()
    if (now - this.lastPlayed < 1000) return
    this.lastPlayed = now
    this.play()
  }

  play() {
    try {
      this.audio?.play()
    } catch (_) {}
  }

  toggle() {
    this.enabled = !this.enabled
    localStorage.setItem("opsAlertsEnabled", this.enabled)
    this.updateToggleUI()
  }

  updateToggleUI() {
    if (!this.hasToggleTarget) return

    // Toggle a class rather than writing textContent. Assigning text used to
    // replace the button's whole contents, deleting the SVG the view renders
    // and leaving a bare emoji sitting among the icon controls.
    this.toggleTarget.classList.toggle("is-muted", !this.enabled)
    this.toggleTarget.setAttribute("aria-label", this.enabled ? "Sound on" : "Sound off")
  }

  createAudio() {
    if (typeof AudioContext === "undefined" && typeof webkitAudioContext === "undefined") return null
    const Ctx = window.AudioContext || window.webkitAudioContext
    const ctx = new Ctx()
    return {
      _ctx: ctx,
      play: () => {
        const o = ctx.createOscillator()
        const g = ctx.createGain()
        o.type = "sine"
        o.frequency.value = 880
        g.gain.setValueAtTime(0.0001, ctx.currentTime)
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01)
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3)
        o.connect(g).connect(ctx.destination)
        o.start()
        o.stop(ctx.currentTime + 0.35)
      }
    }
  }
}
